import { ApartmentOutlined, DeleteOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Empty, Input, InputNumber, Popconfirm, Select, Space, Spin, Typography } from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useAvailableReportPlatformTypes,
  useReportBuilderOptions,
  useSaveReportBuilderOptionsMutation,
} from '../../hooks/useReports'
import { useAuthSession } from '../../lib/session'
import type { FieldOption, FieldOptionUpsert } from '../../types/reports'
import {
  AUTO_FIELD_FORMAT_KIND,
  DEFAULT_FIELD_FORMAT_DECIMALS,
  FIELD_FORMAT_KIND_LABELS,
  MAX_FIELD_FORMAT_DECIMALS,
  formatFieldValue,
  formatKindsForFieldType,
  formatUsesDecimals,
  isFormatKindAllowedForType,
  normalizeFieldFormat,
  resolveDefaultFieldFormat,
  type FieldFormat,
  type FieldFormatKind,
} from '../../lib/fieldFormat'
import { useAuthorization } from '../../contexts/AuthorizationContext'
import { useToast } from '../../contexts/ToastContext'
import { permissions } from '../../types/authorization'
import { downloadText } from '../../lib/download'
import DrilldownHierarchyModal from './DrilldownHierarchyModal'
import { PLATFORM_TYPE_OPTIONS } from './reportOptionConstants'

const FIELD_TYPE_OPTIONS = [
  { value: 'string', label: 'string' },
  { value: 'number', label: 'number' },
  { value: 'date', label: 'date' },
  { value: 'boolean', label: 'boolean' },
  { value: 'booleanString', label: 'booleanString' },
]

const CUSTOM_TYPE_OPTIONS = [
  { value: true, label: 'Custom' },
  { value: false, label: 'All' },
]

const DEFAULT_FORMAT_OPTION = '__default__'

// Sample values for the per-row preview, chosen to show decimals, separators and the % scale.
const FORMAT_PREVIEW_SAMPLES: Record<FieldFormatKind, string | number> = {
  currency: 1234567.891,
  percent: 0.4525,
  number: 1234.5678,
  integer: 1234.5678,
  date: '2026-03-15',
  datetime: '2026-03-15T09:30:00',
  text: 'Shown as is',
}

function describeFormatPreview(row: FieldOption, isMeasure: boolean) {
  const format = row.format ?? resolveDefaultFieldFormat(row.name, row.type, isMeasure)
  const sample = formatFieldValue(FORMAT_PREVIEW_SAMPLES[format.kind], format)
  return row.format ? sample : `Default · ${FIELD_FORMAT_KIND_LABELS[format.kind]} (${sample})`
}

function withFieldType(row: FieldOption, type: string): FieldOption {
  // A format that no longer fits the new type (e.g. Currency on a date) falls back to Default.
  const format = row.format && isFormatKindAllowedForType(row.format.kind, type) ? row.format : null
  return { ...row, type, format }
}

function withFormatKind(row: FieldOption, kind: string): FieldOption {
  if (kind === DEFAULT_FORMAT_OPTION) return { ...row, format: null }
  const format: FieldFormat = { kind: kind as FieldFormatKind }
  if (formatUsesDecimals(kind) && row.format && formatUsesDecimals(row.format.kind) && row.format.decimals != null) {
    format.decimals = row.format.decimals
  }
  return { ...row, format }
}

// Default rows are sent as "auto" so the API clears any stored format; every row on the page is sent on save.
function toUpsertOptions(rows: FieldOption[]): FieldOptionUpsert[] {
  return rows.map((row) => ({ ...row, format: row.format ?? { kind: AUTO_FIELD_FORMAT_KIND } }))
}

// The downloaded JSON leaves Default rows without a format key, matching files made before formatting existed.
function toExportOptions(rows: FieldOption[]) {
  return rows.map(({ format, ...row }) => (format ? { ...row, format } : row))
}

type FieldEditorProps = {
  title: string
  rows: FieldOption[]
  emptyText: string
  isMeasure: boolean
  setRows: React.Dispatch<React.SetStateAction<FieldOption[]>>
  onAdd: () => void
}

type ReportOptionsImportPlatform = {
  platformTypeId: number
  defaultDimensions: FieldOption[]
  defaultMeasures: FieldOption[]
}

function getOptionValidationErrors(dimensions: FieldOption[], measures: FieldOption[]) {
  const errors: string[] = []
  const options = [...dimensions, ...measures]
  const supportedTypes = new Set(FIELD_TYPE_OPTIONS.map((option) => option.value))
  options.forEach((option, index) => {
    if (!option.value?.trim() || !option.name?.trim() || !option.type?.trim()) {
      errors.push(`Option ${index + 1} requires value, name, and type.`)
    } else if (!supportedTypes.has(option.type)) {
      errors.push(`Option ${index + 1} has unsupported type "${option.type}".`)
    } else if (option.format && !isFormatKindAllowedForType(option.format.kind, option.type)) {
      errors.push(`Option "${option.name}" cannot use the ${FIELD_FORMAT_KIND_LABELS[option.format.kind]} format with type "${option.type}".`)
    }
  })

  const seen = new Set<string>()
  options.forEach((option) => {
    const value = option.value?.trim().toLowerCase()
    if (!value) return
    if (seen.has(value)) errors.push(`Duplicate option value "${option.value}".`)
    seen.add(value)
  })
  return errors
}

function asFieldOptions(value: unknown, key: string, errors: string[]): FieldOption[] {
  if (!Array.isArray(value)) {
    errors.push(`${key} must be an array.`)
    return []
  }

  return value.map((item, index) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      errors.push(`${key}[${index}] must be an object.`)
      return { value: '', name: '', type: '', isCustom: true }
    }
    const raw = item as Record<string, unknown>
    const type = typeof raw['type'] === 'string' ? raw['type'].trim() : ''
    const rawFormat = raw['format']
    const format = normalizeFieldFormat(rawFormat, type)
    if (rawFormat !== undefined && rawFormat !== null && !format) {
      errors.push(`${key}[${index}] has a format that is not valid for type "${type}".`)
    }
    return {
      value: typeof raw['value'] === 'string' ? raw['value'].trim() : '',
      name: typeof raw['name'] === 'string' ? raw['name'].trim() : '',
      type,
      isCustom: typeof raw['isCustom'] === 'boolean' ? raw['isCustom'] : true,
      drillLevel: typeof raw['drillLevel'] === 'string' && raw['drillLevel'].trim() ? raw['drillLevel'].trim() : null,
      drillNext: typeof raw['drillNext'] === 'string' && raw['drillNext'].trim() ? raw['drillNext'].trim() : null,
      format,
    }
  })
}

function parseReportOptionsImport(value: unknown): { platform: ReportOptionsImportPlatform | null; errors: string[] } {
  const errors: string[] = []
  const root = value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null
  const entries = Array.isArray(value) ? value : root?.['platforms']
  if (!Array.isArray(entries) || entries.length !== 1) {
    return { platform: null, errors: ['Upload one platform entry at a time.'] }
  }
  const entry = entries[0]
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
    return { platform: null, errors: ['The platform entry must be an object.'] }
  }
  const raw = entry as Record<string, unknown>
  const platformTypeId = Number(raw['platformTypeId'])
  if (!Number.isInteger(platformTypeId) || platformTypeId <= 0) errors.push('platformTypeId must be a positive integer.')
  const defaultDimensions = asFieldOptions(raw['defaultDimensions'], 'defaultDimensions', errors)
  const defaultMeasures = asFieldOptions(raw['defaultMeasures'], 'defaultMeasures', errors)
  errors.push(...getOptionValidationErrors(defaultDimensions, defaultMeasures))
  return { platform: errors.length ? null : { platformTypeId, defaultDimensions, defaultMeasures }, errors }
}

function FieldEditor({ title, rows, emptyText, isMeasure, setRows, onAdd }: FieldEditorProps) {
  const valueWidth = 250
  const nameWidth = 250
  const typeWidth = 150
  const clientWidth = 120
  const formatWidth = 150
  const decimalsWidth = 90
  const previewWidth = 220
  const updateRow = (index: number, update: (row: FieldOption) => FieldOption) =>
    setRows((prev) => prev.map((item, i) => (i === index ? update(item) : item)))

  return (
    <Card
      title={title}
      extra={
        <Button onClick={onAdd}>Add +</Button>
      }
      className="h-full"
    >
      <Space direction="vertical" size={12} className="w-full">
        <div className="max-h-[55vh] overflow-x-auto overflow-y-auto pr-1">
          <div className="sticky top-0 z-10 flex w-full min-w-[1360px] items-center gap-2 bg-white pb-2 font-semibold text-slate-700">
            <span className="w-10 shrink-0" />
            <span className="shrink-0" style={{ width: valueWidth }}>Value</span>
            <span className="shrink-0" style={{ width: nameWidth }}>Name</span>
            <span className="shrink-0" style={{ width: typeWidth }}>Type</span>
            <span className="shrink-0" style={{ width: clientWidth }}>Client</span>
            <span className="shrink-0" style={{ width: formatWidth }}>Format</span>
            <span className="shrink-0" style={{ width: decimalsWidth }}>Decimals</span>
            <span className="shrink-0" style={{ width: previewWidth }}>Preview</span>
          </div>
          <Space direction="vertical" size={8} className="w-full">
            {rows.map((row, index) => (
              <div key={`${title}-${index}`} className="flex min-w-[1360px] items-center gap-2">
                <Popconfirm
                  title={`Remove ${title === 'Dimensions' ? 'dimension' : 'measure'}`}
                  description="Are you sure you want to remove this option?"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                >
                  <Button danger type="text" className="shrink-0 px-0" icon={<DeleteOutlined />} aria-label={`Delete ${title} option`}>
                    
                  </Button>
                </Popconfirm>
                <Input
                  value={row.value}
                  onChange={(event) => {
                    const value = event.target.value
                    setRows((prev) => prev.map((item, i) => (i === index ? { ...item, value } : item)))
                  }}
                  style={{ width: valueWidth }}
                />
                <Input
                  value={row.name}
                  onChange={(event) => {
                    const value = event.target.value
                    setRows((prev) => prev.map((item, i) => (i === index ? { ...item, name: value } : item)))
                  }}
                  style={{ width: nameWidth }}
                />
                <Select
                  value={row.type}
                  options={FIELD_TYPE_OPTIONS}
                  style={{ width: typeWidth }}
                  onChange={(value) => updateRow(index, (item) => withFieldType(item, value))}
                />
                <Select
                  value={row.isCustom}
                  options={CUSTOM_TYPE_OPTIONS}
                  style={{ width: clientWidth }}
                  onChange={(value) =>
                    setRows((prev) => prev.map((item, i) => (i === index ? { ...item, isCustom: value } : item)))
                  }
                />
                <Select
                  value={row.format?.kind ?? DEFAULT_FORMAT_OPTION}
                  options={[
                    { value: DEFAULT_FORMAT_OPTION, label: 'Default' },
                    ...formatKindsForFieldType(row.type).map((kind) => ({ value: kind, label: FIELD_FORMAT_KIND_LABELS[kind] })),
                  ]}
                  style={{ width: formatWidth }}
                  aria-label={`Format for ${row.name || 'option'}`}
                  onChange={(value) => updateRow(index, (item) => withFormatKind(item, value))}
                />
                <InputNumber
                  min={0}
                  max={MAX_FIELD_FORMAT_DECIMALS}
                  precision={0}
                  placeholder={String(DEFAULT_FIELD_FORMAT_DECIMALS)}
                  value={row.format?.decimals ?? null}
                  disabled={!row.format || !formatUsesDecimals(row.format.kind)}
                  style={{ width: decimalsWidth }}
                  aria-label={`Decimals for ${row.name || 'option'}`}
                  onChange={(value) =>
                    updateRow(index, (item) => (item.format ? { ...item, format: { ...item.format, decimals: value ?? null } } : item))
                  }
                />
                <Typography.Text type="secondary" className="shrink-0 truncate" style={{ width: previewWidth }}>
                  {describeFormatPreview(row, isMeasure)}
                </Typography.Text>
              </div>
            ))}
          </Space>
        </div>

        {rows.length === 0 ? <Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} /> : null}
      </Space>
    </Card>
  )
}

export default function ReportOptionsPage() {
  const session = useAuthSession()
  const navigate = useNavigate()
  const clientId = session?.currentClientId || ''
  const { addToast } = useToast()
  const { hasPermission } = useAuthorization()
  // Back to the client's PracIntel flavour: the report builder only when it's the one enabled,
  // Agent Builder otherwise (including when neither is).
  const backPath = hasPermission(permissions.viewNotification) && !hasPermission(permissions.viewAgentBuilder)
    ? '/reports/created'
    : '/reports/dashboard-builder'

  const [platformType, setPlatformType] = useState(2)
  const [dimensionRows, setDimensionRows] = useState<FieldOption[]>([])
  const [measureRows, setMeasureRows] = useState<FieldOption[]>([])
  const [importErrors, setImportErrors] = useState<string[]>([])
  const [drilldownModalOpen, setDrilldownModalOpen] = useState(false)
  const uploadInputRef = useRef<HTMLInputElement | null>(null)
  const pendingImportRef = useRef<{ platformType: number } | null>(null)

  const platformTypesQuery = useAvailableReportPlatformTypes(clientId)
  const builderOptionsQuery = useReportBuilderOptions(clientId, platformType)
  const saveBuilderOptionsMutation = useSaveReportBuilderOptionsMutation()
  const saveDrilldownMutation = useSaveReportBuilderOptionsMutation()

  useEffect(() => {
    const pending = pendingImportRef.current
    if (pending) {
      if (pending.platformType !== platformType) {
        pendingImportRef.current = null
      } else if (builderOptionsQuery.data !== undefined && !builderOptionsQuery.isFetching) {
        pendingImportRef.current = null
        return
      } else {
        return
      }
    }
    setDimensionRows(builderOptionsQuery.data?.fieldDimensionOptions || [])
    setMeasureRows(builderOptionsQuery.data?.fieldMeasureOptions || [])
  }, [builderOptionsQuery.data, builderOptionsQuery.isFetching, platformType])

  const platformOptions = useMemo(
    () => {
      const queryOptions = (platformTypesQuery.data || [])
        .map((item) => ({ value: Number(item.value), label: item.name }))
        .filter((item) => Number.isFinite(item.value) && item.label)

      return queryOptions.length ? queryOptions : PLATFORM_TYPE_OPTIONS
    },
    [platformTypesQuery.data]
  )

  const availablePlatformIds = useMemo(() => {
    if (!platformTypesQuery.isSuccess) return null
    const ids = (platformTypesQuery.data || [])
      .map((item) => Number(item.value))
      .filter((value) => Number.isFinite(value))
    return ids.length ? new Set(ids) : null
  }, [platformTypesQuery.isSuccess, platformTypesQuery.data])

  useEffect(() => {
    if (!platformOptions.length) return
    const firstOption = platformOptions[0]
    if (firstOption && !platformOptions.some((option) => option.value === platformType)) {
      setPlatformType(firstOption.value)
    }
  }, [platformOptions, platformType])

  const handlePlatformChange = (value: number | string) => {
    const nextPlatformType = Number(value)
    setPlatformType(nextPlatformType)
    setDimensionRows([])
    setMeasureRows([])
  }

  const addRow = (type: 'dimension' | 'measure') => {
    const rows = type === 'dimension' ? dimensionRows : measureRows
    if (rows.some((row) => !row.value || !row.name || !row.type)) {
      addToast('Please fill in the existing empty option before adding a new one.', 'warning')
      return
    }

    const updater = type === 'dimension' ? setDimensionRows : setMeasureRows
    updater((prev) => [{ value: '', name: '', type: 'string', isCustom: true }, ...prev])
  }

  const saveBuilderOptions = async () => {
    const validationErrors = getOptionValidationErrors(dimensionRows, measureRows)
    if (validationErrors.length) {
      addToast(validationErrors[0] ?? 'Fix report builder option errors before saving.', 'warning')
      return
    }

    try {
      await saveBuilderOptionsMutation.mutateAsync({
        clientId,
        platformType,
        dimensions: toUpsertOptions(dimensionRows),
        measures: toUpsertOptions(measureRows),
      })
    } catch {
      addToast('Failed to save custom report builder options.', 'error')
      return
    }

    addToast('Custom report builder options saved.', 'success')
    await builderOptionsQuery.refetch()
  }

  const downloadBuilderOptions = () => {
    const validationErrors = getOptionValidationErrors(dimensionRows, measureRows)
    if (validationErrors.length) {
      addToast(validationErrors[0] ?? 'Fix report builder option errors before downloading.', 'warning')
      return
    }

    const platformName = platformOptions.find((option) => option.value === platformType)?.label || `Platform ${platformType}`
    const payload = [
      {
        platformTypeId: platformType,
        platformTypeName: platformName,
        defaultDimensions: toExportOptions(dimensionRows),
        defaultMeasures: toExportOptions(measureRows),
      },
    ]

    downloadText(`${JSON.stringify(payload, null, 2)}\n`, 'report-builder-options.json', 'application/json')
    addToast('Report builder options JSON downloaded.', 'success')
  }

  const handleUploadBuilderOptions = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (file.size > 1024 * 1024) {
      setImportErrors(['The JSON file must be 1 MB or smaller.'])
      return
    }

    try {
      const parsed = parseReportOptionsImport(JSON.parse(await file.text()))
      if (!parsed.platform) {
        setImportErrors(parsed.errors)
        return
      }
      if (availablePlatformIds && !availablePlatformIds.has(parsed.platform.platformTypeId)) {
        const label =
          PLATFORM_TYPE_OPTIONS.find((option) => option.value === parsed.platform!.platformTypeId)?.label
          || `Platform ${parsed.platform.platformTypeId}`
        setImportErrors([`${label} is not available for the current client.`])
        return
      }
      pendingImportRef.current = { platformType: parsed.platform.platformTypeId }
      setPlatformType(parsed.platform.platformTypeId)
      setDimensionRows(parsed.platform.defaultDimensions)
      setMeasureRows(parsed.platform.defaultMeasures)
      setImportErrors([])
      addToast(`Loaded ${file.name}. Review the options and save to apply them.`, 'success')
    } catch {
      setImportErrors(['The selected file is not valid JSON.'])
    }
  }

  return (
    <Space direction="vertical" size={16} className="w-full">
      <Card>
        <Space className="w-full justify-between" align="start">
          <div>
            <Typography.Title level={3} className="!mb-1">
              Report Builder Options
            </Typography.Title>
            <Typography.Text type="secondary">Current client: {session?.currentClientName || 'Unknown'}</Typography.Text>
          </div>
          <Space wrap>
            <Button onClick={() => navigate(backPath)}>Back</Button>
            <Space wrap>
              <Typography.Text strong>Platform</Typography.Text>
              <Select
                value={platformType}
                options={platformOptions}
                onChange={handlePlatformChange}
                style={{ width: 240 }}
                loading={platformTypesQuery.isLoading}
                notFoundContent={platformTypesQuery.isLoading ? 'Loading...' : undefined}
              />
            </Space>
            <Button
              icon={<ApartmentOutlined />}
              onClick={() => setDrilldownModalOpen(true)}
              disabled={builderOptionsQuery.isLoading}
            >
              Manage Drilldown
            </Button>
            <Button type="primary" onClick={() => void saveBuilderOptions()} loading={saveBuilderOptionsMutation.isPending}>
              Save Changes
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={downloadBuilderOptions}
              disabled={builderOptionsQuery.isLoading}
            >
              Download JSON
            </Button>
            <input
              ref={uploadInputRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(event) => void handleUploadBuilderOptions(event)}
            />
            <Button icon={<UploadOutlined />} onClick={() => uploadInputRef.current?.click()}>
              Upload JSON
            </Button>
          </Space>
        </Space>
      </Card>

      {builderOptionsQuery.isError ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load report builder options"
          description={builderOptionsQuery.error instanceof Error ? builderOptionsQuery.error.message : 'Unknown error'}
          action={<Button onClick={() => builderOptionsQuery.refetch()}>Retry</Button>}
        />
      ) : null}

      {importErrors.length ? (
        <Alert
          type="error"
          showIcon
          message="Report options JSON could not be loaded"
          description={<ul className="mb-0 pl-4">{importErrors.map((error) => <li key={error}>{error}</li>)}</ul>}
        />
      ) : null}

      <Spin
        spinning={builderOptionsQuery.isLoading || builderOptionsQuery.isFetching}
        tip="Loading report builder options..."
        size="large"
      >
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <FieldEditor
            title="Dimensions"
            rows={dimensionRows}
            emptyText="No dimensions available for the selected platform."
            isMeasure={false}
            setRows={setDimensionRows}
            onAdd={() => addRow('dimension')}
          />
          <FieldEditor
            title="Measures"
            rows={measureRows}
            emptyText="No measures available for the selected platform."
            isMeasure
            setRows={setMeasureRows}
            onAdd={() => addRow('measure')}
          />
        </div>

        {!builderOptionsQuery.isLoading && !builderOptionsQuery.data && !builderOptionsQuery.isError ? (
          <Alert
            className="mt-4"
            type="info"
            showIcon
            message="No report builder options found for the selected platform."
          />
        ) : null}
      </Spin>

      <DrilldownHierarchyModal
        open={drilldownModalOpen}
        dimensions={builderOptionsQuery.data?.fieldDimensionOptions ?? dimensionRows}
        isSaving={saveDrilldownMutation.isPending}
        onClose={() => setDrilldownModalOpen(false)}
        onApply={async (nextDimensions) => {
          // Refuse to save when the server's canonical builder options haven't loaded yet —
          // sending empty arrays in that state would wipe every saved dimension/measure. The
          // user can retry once the query settles.
          const canonicalDimensions = builderOptionsQuery.data?.fieldDimensionOptions
          const canonicalMeasures = builderOptionsQuery.data?.fieldMeasureOptions
          if (!canonicalDimensions || !canonicalMeasures) {
            addToast('Report builder options are still loading. Try again in a moment.', 'error')
            return
          }
          try {
            // Only persist the drilldown-hierarchy edits. The modal was seeded from canonical
            // dimensions above and only rewrites drillLevel/drillNext, so nextDimensions is
            // safe to send: unsaved edits elsewhere on the page (dimension labels, measure
            // rows) aren't silently committed.
            await saveDrilldownMutation.mutateAsync({
              clientId,
              platformType,
              dimensions: nextDimensions,
              measures: canonicalMeasures,
            })
          } catch {
            addToast('Failed to save drilldown hierarchy.', 'error')
            return
          }
          setDrilldownModalOpen(false)
          addToast('Drilldown hierarchy saved.', 'success')
          await builderOptionsQuery.refetch()
        }}
      />
    </Space>
  )
}
